import {
    CapacityModel,
    ScenarioAttributesDto,
    ScenarioPublicationStatus,
    ScenarioTvMatchingKind,
    ScenarioType,
    TimePeriod,
} from '../../../../model/CORE';
import { SINGLE_SELECTION, SingleSelection } from '../../../../common/table/ManagedTable';
import { Flex } from '../../../common/Flex';
import ScenarioTable, { SCENARIO_SELECTED_COLUMN } from '../ScenarioTable';
import { SCENARIO_ATTRIBUTES_SIMPLIFY_COLUMNS, ScenarioAttributesColumn } from '../../../table/ScenarioAttributesTable';
import React, { PureComponent } from 'react';
import TimeUtils from '../../../../common/util/TimeUtils';
import { LabelDisplayMode, TimePeriodSelector } from '../../../map/control/TimePeriodSelector';
import memoizeOne from 'memoize-one';
import AppContext from '../../../../common/context/AppContext';
import { Checkbox, Form, Input, notification, Select, Space } from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import { ScenarioDisplayMode } from '../ApplicableScenariosPanel';
import { EntryLoadBar } from '../../../dashboard/network/flows/svg/EntryLoadBar';
import { ServiceFactory } from '../../../../service/ServiceFactory';
import { ExtendedMonitoredTv } from '../../../../model/SOL49';
import { OccupancyLoadBar } from '../../../dashboard/network/flows/svg/OccupancyLoadBar';
import Utils from 'app/common/util/Utils';

interface ScenarioMainPanelProps {
    scenarios: ScenarioAttributesDto[];
    onSelectScenario: (scenario: ScenarioAttributesDto) => void;
    selectedTimePeriod: TimePeriod;
    selectedScenario?: ScenarioAttributesDto;
    displayMode: ScenarioDisplayMode;
    selectedTvIds?: string[];
    onSelectedTimePeriodChange: (tp: TimePeriod) => void;
}

interface ScenarioMainPanelState {
    isTvHeaderLoading: boolean;
    selectedScenario?: ScenarioAttributesDto;
    selectedTimePeriod?: TimePeriod;
    scenarioFilterId?: string;
    scenarioFilterType?: string;
    scenarioFilterStatus?: string;
    scenarioFilterMatchingKind?: string;
    scenarioFilterZeroFlight?: boolean;
    monitoredTvs?: ExtendedMonitoredTv[];
}

export default class ScenarioMainPanel extends PureComponent<ScenarioMainPanelProps, ScenarioMainPanelState> {
    private scenarioSelection = memoizeOne(
        (selection): SingleSelection<ScenarioAttributesDto> => ({
            selectionMode: SINGLE_SELECTION,
            onSelectionChange: this.onScenarioSelected,
            selectedItem: selection,
            selectionMatcher: (element: ScenarioAttributesDto, currentSelection: ScenarioAttributesDto) => {
                return (
                    currentSelection != null && element != null && element.scenarioId === currentSelection.scenarioId
                );
            },
        }),
    );

    constructor(props: ScenarioMainPanelProps) {
        super(props);
        this.state = {
            selectedTimePeriod: this.props.selectedTimePeriod,
            isTvHeaderLoading: true,
            scenarioFilterZeroFlight: this.props.displayMode === ScenarioDisplayMode.POLYGON_SEARCH,
            scenarioFilterStatus: ScenarioPublicationStatus[ScenarioPublicationStatus.PUBLISHED],
        };
    }

    componentDidMount() {
        if (this.props.selectedTvIds) {
            this.getTvLoadAndPeriods();
        }
    }

    componentDidUpdate(prevProps: Readonly<ScenarioMainPanelProps>) {
        if (prevProps.selectedScenario !== this.props.selectedScenario) {
            this.setState({
                selectedScenario: this.props.selectedScenario,
            });
        }
    }

    private getTvLoadAndPeriods() {
        this.setState({ isTvHeaderLoading: true });
        ServiceFactory.sol49Service()
            .getMonitoringData({
                capacityModel: CapacityModel.CCM,
                tvsWithTimePeriod: this.props.selectedTvIds.map(selectedTvId => {
                    return { tvId: selectedTvId, timePeriod: this.getFullDayTimePeriod() };
                }),
            })
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                } else {
                    this.setState({
                        monitoredTvs: reply.data.monitoredTvs,
                        isTvHeaderLoading: false,
                    });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    }

    private getFullDayTimePeriod() {
        const timeFrom = TimeUtils.withoutMinutes(TimeUtils.withoutSeconds(AppContext.getCurrentTime()));
        timeFrom.setUTCHours(0);
        const timeTo = TimeUtils.withoutMinutes(TimeUtils.withoutSeconds(AppContext.getCurrentTime()));
        timeTo.setUTCHours(24);
        return { from: timeFrom, to: timeTo };
    }

    /**
     * HANDLERS
     */
    private onScenarioSelected = (scenario: ScenarioAttributesDto) => {
        this.setState({ selectedScenario: scenario });
        this.props.onSelectScenario(scenario);
    };

    private onSelectedTimePeriodChange = (selectedTimePeriod: TimePeriod) => {
        this.setState({
            selectedTimePeriod: selectedTimePeriod,
        });
    };

    /**
     * Scenarios Filters
     */

    private filterByPublicationId(scenarios: ScenarioAttributesDto[], filterValue: string): ScenarioAttributesDto[] {
        if (Utils.isEmptyString(filterValue)) {
            return scenarios;
        } else {
            const textFilter = Utils.toUpperCase(filterValue);
            const re = Utils.wildcardToRegex(textFilter);
            return scenarios
                .filter(scenario => scenario.publicationId != null)
                .filter(scenario => re.test(Utils.toUpperCase(scenario.publicationId)));
        }
    }

    private toMatchingKindList(scenario: ScenarioAttributesDto): ScenarioTvMatchingKind[] {
        const scenarioTvMatchingKinds: ScenarioTvMatchingKind[] = [];
        for (const [_, detail] of Object.entries(scenario.scenarioTvCaptureDetailsByTv ?? {})) {
            scenarioTvMatchingKinds.push(detail.matchingKind);
        }
        return scenarioTvMatchingKinds;
    }

    private filterByMatchingKind(scenarios: ScenarioAttributesDto[], filterValue: string): ScenarioAttributesDto[] {
        if (filterValue == null || filterValue === 'ALL') {
            return scenarios;
        } else {
            return scenarios
                .filter(
                    scenario =>
                        this.toMatchingKindList(scenario) != null && this.toMatchingKindList(scenario).length > 0,
                )
                .filter(scenario =>
                    this.toMatchingKindList(scenario)
                        .map(value => value.toString())
                        .includes(filterValue),
                );
        }
    }

    private filterByScenarioType(scenarios: ScenarioAttributesDto[], filterValue: string): ScenarioAttributesDto[] {
        if (filterValue == null || filterValue === 'ALL') {
            return scenarios;
        } else if (filterValue.toLowerCase() === 'aw') {
            return scenarios.filter(scenario => scenario.publicationId.toLowerCase().startsWith('aw'));
        } else if (filterValue.toLowerCase() === 'rn') {
            return scenarios.filter(scenario => scenario.publicationId.toLowerCase().startsWith('rn'));
        } else {
            return scenarios
                .filter(scenario => scenario.scenarioType != null)
                .filter(scenario => scenario.scenarioType === filterValue);
        }
    }

    private filterByScenarioStatus(scenarios: ScenarioAttributesDto[], filterValue: string): ScenarioAttributesDto[] {
        if (filterValue == null || filterValue === 'ALL') {
            return scenarios;
        } else {
            return scenarios
                .filter(scenario => scenario.publicationStatus != null)
                .filter(scenario => scenario.publicationStatus === filterValue);
        }
    }

    private filterScenarios = memoizeOne(
        (
            id: string,
            type: string,
            status: string,
            matchingKind: string,
            filter0FlightScenario: boolean,
            scenarios: ScenarioAttributesDto[],
        ) => {
            scenarios = this.filterByScenarioType(scenarios, type);
            scenarios = this.filterByScenarioStatus(scenarios, status);
            scenarios = this.filterByPublicationId(scenarios, id);
            scenarios = this.filterByMatchingKind(scenarios, matchingKind);
            if (filter0FlightScenario) {
                scenarios = scenarios.filter(scenario => scenario.scenarioCapturedFlights !== 0);
            }
            return scenarios;
        },
    );

    /**
     * RENDERS
     */
    render() {
        const scenarios = this.filterScenarios(
            this.state.scenarioFilterId,
            this.state.scenarioFilterType,
            this.state.scenarioFilterStatus,
            this.state.scenarioFilterMatchingKind,
            this.state.scenarioFilterZeroFlight,
            this.props.scenarios,
        );
        const columns = [SCENARIO_SELECTED_COLUMN, ...SCENARIO_ATTRIBUTES_SIMPLIFY_COLUMNS];
        if (
            scenarios.find(
                scenario =>
                    scenario.scenarioTvCaptureDetailsByTv != null &&
                    Object.keys(scenario.scenarioTvCaptureDetailsByTv ?? {}).length > 0,
            )
        ) {
            columns.push(ScenarioAttributesColumn.MATCHING_KIND);
        }
        if (this.props.displayMode === ScenarioDisplayMode.PROBLEMATIC_TV) {
            columns.push(ScenarioAttributesColumn.TV_CAPTURED_FLIGHTS);
            columns.push(ScenarioAttributesColumn.OTHER_CAPTURED_FLIGHTS);
        } else if (this.props.displayMode === ScenarioDisplayMode.POLYGON_SEARCH) {
            columns.push(ScenarioAttributesColumn.SCENARIO_CAPTURED_FLIGHTS);
        }
        const fullDayTimePeriod = this.getFullDayTimePeriod();
        const getTvCaptureDetails = (tvId: string) =>
            this.state.monitoredTvs?.length > 1
                ? this.props.selectedScenario?.scenarioTvCaptureDetailsByTv[tvId]
                : null;
        return (
            <Flex grow={true} className="applicable-scenarios-panel" direction="vertical">
                {this.props.selectedTvIds && (
                    <Flex direction={'vertical'} grow={false} style={{ gap: '1em' }}>
                        <Flex grow={false} className={'scenario-title'}>
                            Scenarios by TVs
                        </Flex>
                        {!this.state.isTvHeaderLoading && (
                            <Flex
                                grow={false}
                                direction={'vertical'}
                                style={{
                                    gap: '.4em',
                                    padding: '.4em',
                                    maxHeight: '30em',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                }}>
                                {this.state.monitoredTvs.map((monitoredTv: ExtendedMonitoredTv) => {
                                    return (
                                        <Flex
                                            direction={'vertical'}
                                            grow={false}
                                            style={{ gap: '.25em' }}
                                            key={monitoredTv.refLocation.id}
                                            className={
                                                getTvCaptureDetails(monitoredTv.refLocation.id)
                                                    ? 'tv-loadbar selected'
                                                    : 'tv-loadbar'
                                            }>
                                            <Flex>
                                                {monitoredTv.refLocation.id}{' '}
                                                {getTvCaptureDetails(monitoredTv.refLocation.id)
                                                    ? ` (${ScenarioAttributesColumn.getMatchingKindLabel(getTvCaptureDetails(monitoredTv.refLocation.id).matchingKind)} | ${getTvCaptureDetails(monitoredTv.refLocation.id).flightCount} FLIGHTS)`
                                                    : ''}
                                            </Flex>
                                            <EntryLoadBar
                                                drawBottomLine={true}
                                                loadValues={monitoredTv.loadValues}
                                                monitoredPeriod={fullDayTimePeriod}
                                                activePeriods={[this.state.selectedTimePeriod]}
                                                currentTime={AppContext.getCurrentTime()}
                                                defaultSelectedPeriod={this.state.selectedTimePeriod}
                                                showLoadInactivePeriods={true}
                                                label={'E'}
                                                barHeightEm={1}
                                            />
                                            {monitoredTv.occupancyLoadValues &&
                                                monitoredTv.occupancyLoadValues.map(occupancyValues => {
                                                    return (
                                                        <OccupancyLoadBar
                                                            key={'occupancy-load-' + occupancyValues.durationInMin}
                                                            drawBottomLine={true}
                                                            loadValues={occupancyValues.occupancyLoadValues}
                                                            monitoredPeriod={fullDayTimePeriod}
                                                            activePeriods={[this.state.selectedTimePeriod]}
                                                            currentTime={AppContext.getCurrentTime()}
                                                            defaultSelectedPeriod={this.state.selectedTimePeriod}
                                                            showLoadInactivePeriods={true}
                                                            label={'O'}
                                                            barHeightEm={1}
                                                        />
                                                    );
                                                })}
                                        </Flex>
                                    );
                                })}
                            </Flex>
                        )}
                        {this.state.isTvHeaderLoading && <span>Loading...</span>}
                        <Flex grow={false} direction="vertical" style={{ gap: '1em', padding: '.4em' }}>
                            {this.renderHeaderTimeSlider(fullDayTimePeriod)}
                        </Flex>
                    </Flex>
                )}
                {!this.props.selectedTvIds && (
                    <Flex direction={'vertical'} grow={false}>
                        <Flex size="2em" className={'scenario-title'}>
                            Scenarios repository
                        </Flex>
                        <Flex direction="vertical" style={{ gap: '1em' }}>
                            {this.renderHeaderTimeSlider(fullDayTimePeriod)}
                        </Flex>
                    </Flex>
                )}
                <Flex grow={false} direction="vertical" style={{ gap: '1em', marginTop: '1em' }}>
                    {this.renderHeaderFilters()}
                </Flex>
                <Flex grow={true} style={{ minHeight: '30em' }} direction="vertical">
                    <ScenarioTable
                        data={scenarios}
                        noDataMessage={'No scenario found'}
                        selection={this.scenarioSelection(this.state.selectedScenario)}
                        columnResizeDisabled={true}
                        columns={columns}
                    />
                </Flex>
            </Flex>
        );
    }

    renderHeaderTimeSlider(fullDayTimePeriod: TimePeriod) {
        const currentTimeWithoutSeconds = TimeUtils.withoutSeconds(AppContext.getCurrentTime());
        return (
            <>
                <Flex grow={false}>
                    <span className="detailed-subpanel-title">TIME PERIOD</span>
                </Flex>
                <Flex direction={'vertical'} className={'tv-loadbar'} grow={false}>
                    <Flex style={{ alignItems: 'center' }}>
                        <TimePeriodSelector
                            width="23em"
                            value={this.state.selectedTimePeriod}
                            labelDisplayMode={LabelDisplayMode.ABSOLUTE}
                            currentTime={currentTimeWithoutSeconds}
                            markIntervalMs={60 * TimeUtils.MIN_TO_MS}
                            currentTimeLineColor="#E6E6E6"
                            timePeriodRange={fullDayTimePeriod}
                            hideDownLabels={true}
                            hasManualHandler
                            onChange={this.onSelectedTimePeriodChange}
                            onSelectedPeriodConfirmed={(tp: TimePeriod) => {
                                this.onSelectedTimePeriodChange(tp);
                                this.props.onSelectedTimePeriodChange(tp);
                            }}
                        />
                    </Flex>
                </Flex>
            </>
        );
    }

    renderHeaderFilters() {
        return (
            <>
                <Flex grow={false}>
                    <span className="detailed-subpanel-title">FILTERS</span>
                </Flex>
                <Flex grow={false}>
                    <Form size={'small'}>
                        <Space.Compact>
                            <FormItem noStyle label={'filter'}>
                                <Input
                                    placeholder={'Search...'}
                                    style={{ width: '5em' }}
                                    onChange={e => this.setState({ scenarioFilterId: e.target.value })}
                                />
                            </FormItem>
                            <FormItem noStyle>
                                <Select
                                    value={this.state.scenarioFilterType ? this.state.scenarioFilterType : 'ALL'}
                                    style={{ width: '5em' }}
                                    onChange={val => this.setState({ scenarioFilterType: val })}>
                                    <Select.Option value={'ALL'}>All</Select.Option>
                                    <Select.Option value={ScenarioType[ScenarioType.RR]}>
                                        {ScenarioType[ScenarioType.RR]}
                                    </Select.Option>
                                    <Select.Option value={ScenarioType[ScenarioType.FL]}>
                                        {ScenarioType[ScenarioType.FL]}
                                    </Select.Option>
                                    <Select.Option value={ScenarioType[ScenarioType.AR]}>
                                        {ScenarioType[ScenarioType.AR]}
                                    </Select.Option>
                                    <Select.Option value={ScenarioType[ScenarioType.STAM]}>
                                        {ScenarioType[ScenarioType.STAM]}
                                    </Select.Option>
                                    <Select.Option value={ScenarioType[ScenarioType.MIXED]}>
                                        {ScenarioType[ScenarioType.MIXED]}
                                    </Select.Option>
                                    <Select.Option value={'AW'}>AW</Select.Option>
                                    <Select.Option value={'RN'}>RN</Select.Option>
                                </Select>
                            </FormItem>
                            <FormItem noStyle>
                                <Select
                                    value={this.state.scenarioFilterStatus ? this.state.scenarioFilterStatus : 'ALL'}
                                    style={{ width: '7em' }}
                                    onChange={val => this.setState({ scenarioFilterStatus: val })}>
                                    <Select.Option value="ALL">All</Select.Option>
                                    <Select.Option value={ScenarioPublicationStatus[ScenarioPublicationStatus.DRAFT]}>
                                        {ScenarioPublicationStatus[ScenarioPublicationStatus.DRAFT]}
                                    </Select.Option>
                                    <Select.Option
                                        value={ScenarioPublicationStatus[ScenarioPublicationStatus.PUBLISHED]}>
                                        {ScenarioPublicationStatus[ScenarioPublicationStatus.PUBLISHED]}
                                    </Select.Option>
                                </Select>
                            </FormItem>
                            {this.props.displayMode !== ScenarioDisplayMode.SIMPLE && (
                                <FormItem noStyle>
                                    <Select
                                        value={
                                            this.state.scenarioFilterMatchingKind
                                                ? this.state.scenarioFilterMatchingKind
                                                : 'ALL'
                                        }
                                        style={{ width: '8em' }}
                                        onChange={val => this.setState({ scenarioFilterMatchingKind: val })}>
                                        <Select.Option value="ALL">All</Select.Option>
                                        {this.props.displayMode === ScenarioDisplayMode.PROBLEMATIC_TV && (
                                            <Select.Option
                                                value={
                                                    ScenarioTvMatchingKind[ScenarioTvMatchingKind.SAME_TRAFFIC_VOLUME]
                                                }>
                                                Same TV
                                            </Select.Option>
                                        )}
                                        {this.props.displayMode === ScenarioDisplayMode.PROBLEMATIC_TV && (
                                            <Select.Option
                                                value={
                                                    ScenarioTvMatchingKind[
                                                        ScenarioTvMatchingKind.SAME_REFERENCE_LOCATION
                                                    ]
                                                }>
                                                Same Refloc
                                            </Select.Option>
                                        )}
                                        <Select.Option
                                            value={
                                                ScenarioTvMatchingKind[
                                                    ScenarioTvMatchingKind.OVERLAPPING_REFERENCE_LOCATION
                                                ]
                                            }>
                                            Overlapping
                                        </Select.Option>
                                        <Select.Option
                                            value={ScenarioTvMatchingKind[ScenarioTvMatchingKind.INDIRECT_OFFLOAD]}>
                                            Indirect
                                        </Select.Option>
                                    </Select>
                                </FormItem>
                            )}
                        </Space.Compact>
                    </Form>
                </Flex>
                {this.props.displayMode === ScenarioDisplayMode.POLYGON_SEARCH && (
                    <Flex grow={false} style={{ marginBottom: '5px' }}>
                        <Form>
                            <Space.Compact>
                                <FormItem noStyle>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Checkbox
                                            checked={this.state.scenarioFilterZeroFlight}
                                            onChange={val => {
                                                this.setState({ scenarioFilterZeroFlight: val.target.checked });
                                            }}
                                        />
                                        <span>hide 0 flights caught</span>
                                    </div>
                                </FormItem>
                            </Space.Compact>
                        </Form>
                    </Flex>
                )}
            </>
        );
    }
}
